<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useNoteStore } from '@/modules/investing/stores/note-store'
import { VTextField } from '@/modules/shared/components'
import NoteForm from './note-form.vue'
import NoteItem from './note-item.vue'

const props = withDefaults(
  defineProps<{
    commentableType: string
    commentableId: string
    permission?: string
    allowReplies?: boolean
  }>(),
  {
    allowReplies: true,
  },
)

const { slug } = useRoute().params as { slug: string }
const authStore = useAuthStore()
const noteStore = useNoteStore()

const searchQuery = ref('')

const currentUserId = computed(() => authStore.current_user?.id)
const isGroupAdmin = computed(() => authStore.is_site_or_group_admin)

const filteredNotes = computed(() => {
  if (!searchQuery.value.trim()) return noteStore.notes

  const query = searchQuery.value.toLowerCase()
  return noteStore.notes.filter((note) => {
    return note.text.toLowerCase().includes(query) || note.author_name.toLowerCase().includes(query)
  })
})

const handleCreate = async (payload: { text: string; permission?: string }) => {
  if (props.permission) {
    payload.permission = props.permission
  }
  await noteStore.createNote(props.commentableType, props.commentableId, payload, { slug })
}

const handleUpdate = async (noteId: number, payload: { text: string }) => {
  await noteStore.updateNote(noteId, payload, { slug })
}

const handleDelete = async (noteId: number) => {
  await noteStore.deleteNote(noteId, { slug })
}

const handleReply = (parentNote: { id: number; permission: string }) => {
  return async (payload: { text: string; permission?: string }) => {
    // Replies are comments on comments — the commentable is the parent note
    await noteStore.createNote(
      'comments',
      String(parentNote.id),
      {
        text: payload.text,
        permission: props.permission || parentNote.permission,
      },
      { slug },
    )
    // Refresh to get updated replies
    await noteStore.fetchNotes(props.commentableType, props.commentableId, { slug })
  }
}
</script>

<template>
  <div class="space-y-4">
    <!-- Create form -->
    <NoteForm mode="create" :permission="permission" :onSubmit="handleCreate" />

    <!-- Search -->
    <div v-if="noteStore.notes.length > 0">
      <VTextField v-model="searchQuery" label="" placeholder="Search notes by text or author..." type="text" />
    </div>

    <!-- Loading -->
    <div v-if="noteStore.loading" class="py-8 text-center text-sm text-gray-500">Loading notes...</div>

    <!-- Notes list -->
    <template v-else>
      <div
        v-if="filteredNotes.length === 0 && noteStore.notes.length > 0"
        class="py-8 text-center text-sm text-gray-500"
      >
        No notes match your search.
      </div>

      <div v-else-if="filteredNotes.length === 0" class="py-8 text-center text-sm text-gray-500">
        No notes yet. Add the first one above.
      </div>

      <NoteItem
        v-for="note in filteredNotes"
        :key="note.id"
        :note="note"
        :commentableType="commentableType"
        :commentableId="commentableId"
        :permission="permission"
        :allowReplies="allowReplies"
        :depth="0"
        :currentUserId="currentUserId"
        :isGroupAdmin="isGroupAdmin"
        :onUpdate="handleUpdate"
        :onDelete="handleDelete"
        :onReply="handleReply(note)"
      />
    </template>
  </div>
</template>
